import React from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from './FAQSection';
import './WholesaleDistributionPage.css';

const WholesaleDistributionPage = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [featuresRef, featuresInView] = useInView({ threshold: 0.1 });
  const [benefitsRef, benefitsInView] = useInView({ threshold: 0.1 });
  const [processRef, processInView] = useInView({ threshold: 0.1 });

  const fadeInUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  // const slideInRight = {
  //   hidden: { opacity: 0, x: 50 },
  //   visible: {
  //     opacity: 1,
  //     x: 0,
  //     transition: { duration: 0.8, ease: "easeOut" }
  //   }
  // };

  const keyFeatures = [
    {
      icon: "bi-boxes",
      title: "Advanced Inventory Management",
      description: "Real-time inventory tracking, demand forecasting, and automated reordering with multi-location support.",
      color: "#0066CC"
    },
    {
      icon: "bi-truck",
      title: "Order Management & Fulfillment",
      description: "Streamlined order processing, picking, packing, and shipping with carrier integration and tracking.",
      color: "#1E40AF"
    },
    {
      icon: "bi-diagram-3",
      title: "Supply Chain Optimization",
      description: "Vendor management, purchase order automation, and supply chain visibility for optimal operations.",
      color: "#0066CC"
    },
    {
      icon: "bi-graph-up-arrow",
      title: "Financial Management & Accounting",
      description: "Integrated accounting, cost tracking, margin analysis, and comprehensive financial reporting.",
      color: "#1E40AF"
    },
    {
      icon: "bi-people-fill",
      title: "Customer Relationship Management",
      description: "Customer portal, pricing management, loyalty programs, and comprehensive sales analytics.",
      color: "#0066CC"
    },
    {
      icon: "bi-bar-chart",
      title: "Business Intelligence & Analytics",
      description: "Real-time dashboards, performance metrics, and predictive analytics for informed decision-making.",
      color: "#1E40AF"
    }
  ];

  const benefits = [
    {
      icon: "bi-shop",
      title: "Wholesale Distributors",
      description: "Optimize bulk inventory management, B2B customer relationships, and multi-channel distribution networks.",
      stats: "35% inventory reduction"
    },
    {
      icon: "bi-truck-flatbed",
      title: "Logistics & 3PL Providers",
      description: "Streamline warehouse operations, freight management, and client billing with integrated tracking systems.",
      stats: "50% operational efficiency"
    },
    {
      icon: "bi-building-gear",
      title: "Industrial Supply Companies",
      description: "Manage complex product catalogs, technical specifications, and specialized customer requirements.",
      stats: "40% order accuracy"
    },
    {
      icon: "bi-basket2",
      title: "Food & Beverage Distributors",
      description: "Handle perishable inventory, compliance tracking, and temperature-controlled supply chain management.",
      stats: "25% waste reduction"
    },
    {
      icon: "bi-tools",
      title: "Equipment & Machinery Distributors",
      description: "Track warranties, manage service contracts, and optimize parts inventory for heavy equipment distribution.",
      stats: "60% service efficiency"
    },
    {
      icon: "bi-bag-check",
      title: "Consumer Goods Distributors",
      description: "Manage seasonal demand, promotional campaigns, and retail partner relationships with comprehensive analytics.",
      stats: "45% sales growth"
    }
  ];

  const processSteps = [
    {
      title: "Improved Order Accuracy",
      description: "Reduce errors and enhance customer satisfaction with automated order processing",
      icon: "bi-check-circle"
    },
    {
      title: "Optimized Inventory Levels",
      description: "Minimize carrying costs while ensuring product availability across all locations",
      icon: "bi-bar-chart-steps"
    },
    {
      title: "Enhanced Supply Chain Visibility",
      description: "Track products from supplier to customer with real-time visibility and reporting",
      icon: "bi-eye"
    },
    {
      title: "Increased Profitability",
      description: "Optimize pricing, reduce costs, and improve margins through data-driven insights",
      icon: "bi-graph-up"
    }
  ];

  return (
    <div className="wholesale-distribution-page">
      <Helmet>
        <title>NetSuite for Wholesale & Distribution USA | SpireNest</title>
        <link rel="canonical" href="https://spirenest.co/netsuite/industries/wholesale-distribution" />
        <meta name="description" content="Streamline your wholesale and distribution operations with SpireNest's NetSuite solutions, optimized for the U.S. supply chain sector." />
        <meta name="keywords" content="NetSuite wholesale, distribution ERP, supply chain management, USA, SpireNest" />
        <meta property="og:title" content="NetSuite for Wholesale & Distribution | SpireNest USA" />
        <meta property="og:description" content="Streamline inventory, logistics, and order management with NetSuite ERP for distributors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://spirenest.co/industries/wholesale-distribution" />
        <meta property="og:image" content="https://spirenest.co/assets/images/og-wholesale.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="NetSuite for Wholesale & Distribution | SpireNest USA" />
        <meta name="twitter:description" content="End-to-end ERP solutions to manage your wholesale and distribution operations." />
        <meta name="twitter:image" content="https://spirenest.co/assets/images/og-wholesale.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="hero-section position-relative overflow-hidden">
        <div className="hero-bg-overlay"></div>
        {/* <div className="floating-shapes">
          <motion.div className="shape-1" animate={{ rotate: 360, y: [-20, 20] }} transition={{ duration: 20, repeat: Infinity }} />
          <motion.div className="shape-2" animate={{ rotate: -360, x: [-30, 30] }} transition={{ duration: 25, repeat: Infinity }} />
          <motion.div className="shape-3" animate={{ y: [-15, 15], rotate: 180 }} transition={{ duration: 15, repeat: Infinity }} />
        </div> */}
        
        <Container className="position-relative">
          <Row className="align-items-center min-vh-100">
            <Col lg={7}>
              <motion.div
                ref={heroRef}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                variants={staggerContainer}
                className="hero-content"
              >
                <motion.div variants={fadeInUp}>
                  <Badge className="hero-badge mb-4">
                    <i className="bi-truck me-2"></i>
                    WHOLESALE & DISTRIBUTION INDUSTRY
                  </Badge>
                </motion.div>
                
                <motion.h1 variants={slideInLeft} className="hero-title">
                  NetSuite ERP for <span style={{background: 'linear-gradient(135deg, #FF8F00, #FF6B35)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>Wholesale & Distribution</span>
                </motion.h1>
                
                <motion.p variants={slideInLeft} className="hero-description">
                  Transform your wholesale and distribution operations with our comprehensive NetSuite ERP solution. 
                  Optimize inventory management, streamline order fulfillment, and enhance supply chain visibility. 
                  From procurement to delivery, manage every aspect of your distribution business with precision, 
                  efficiency, and real-time insights for maximum profitability.
                </motion.p>
              </motion.div>
            </Col>
            
            <Col lg={5}>
              <motion.div
                initial={{ opacity: 0, scale: 0.8, rotateY: 45 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="hero-visual"
              >
                <div className="distribution-visual">
                  <motion.div 
                    className="truck-container"
                    animate={{ x: [0, 20, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-truck truck-icon" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                  <motion.div 
                    className="warehouse-small-1"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-building" style={{color: '#FF6B35'}}></i>
                  </motion.div>
                  <motion.div 
                    className="boxes-small-2"
                    animate={{ y: [-5, 5] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <i className="bi-boxes" style={{color: '#FF8F00'}}></i>
                  </motion.div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features Section */}
      <section className="features-section py-5">
        <Container>
          <motion.div
            ref={featuresRef}
            initial="hidden"
            animate={featuresInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Key Features of NetSuite</h2>
              <p className="section-description">
                Comprehensive wholesale and distribution capabilities designed for modern supply chain businesses
              </p>
            </motion.div>
            
            <Row className="g-4">
              {keyFeatures.map((feature, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Card className="feature-card h-100 border-0">
                      <Card.Body className="p-4">
                        <motion.div 
                          className="feature-icon mb-3"
                          style={{ backgroundColor: `${feature.color}15`, color: feature.color }}
                          whileHover={{ rotate: 360, scale: 1.1 }}
                          transition={{ duration: 0.5 }}
                        >
                          <i className={`bi ${feature.icon}`}></i>
                        </motion.div>
                        <h5 className="feature-title mb-3">{feature.title}</h5>
                        <p className="feature-description">{feature.description}</p>
                        
                        <motion.div
                          className="feature-accent"
                          style={{ background: feature.color }}
                          initial={{ width: 0 }}
                          whileInView={{ width: "60px" }}
                          transition={{ duration: 0.8, delay: index * 0.1 }}
                        />
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section py-5">
        <Container>
          <motion.div
            ref={benefitsRef}
            initial="hidden"
            animate={benefitsInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <Row className="mb-5">
              <Col lg={12}>
                <motion.div variants={slideInLeft} className="text-center">
                  <h2 className="section-title">Who Benefits from NetSuite for Wholesale & Distribution</h2>
                  <p className="section-description">
                    Discover how different distribution sectors leverage NetSuite to optimize operations, 
                    improve efficiency, and achieve supply chain excellence across diverse market segments.
                  </p>
                </motion.div>
              </Col>
            </Row>
            
            <Row className="g-4">
              {benefits.map((benefit, index) => (
                <Col lg={4} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ scale: 1.05 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="h-100"
                  >
                    <Card className="benefit-card h-100 border-0">
                      <Card.Body className="p-4 text-center">
                        <motion.div 
                          className="benefit-icon mb-3"
                          whileHover={{ rotate: [0, -10, 10, 0], scale: 1.1 }}
                        >
                          <i className={`bi ${benefit.icon}`}></i>
                        </motion.div>
                        <h5 className="benefit-title mb-3">{benefit.title}</h5>
                        <p className="benefit-description mb-3">{benefit.description}</p>
                        <Badge className="stats-badge">{benefit.stats}</Badge>
                      </Card.Body>
                      
                      <motion.div
                        className="card-accent"
                        initial={{ scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                      />
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Process Benefits Section */}
      <section className="process-section py-5">
        <Container>
          <motion.div
            ref={processRef}
            initial="hidden"
            animate={processInView ? "visible" : "hidden"}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="section-header text-center mb-5">
              <h2 className="section-title">Transform Your Distribution Operations</h2>
            </motion.div>
            
            <Row className="g-4">
              {processSteps.map((step, index) => (
                <Col lg={3} md={6} key={index}>
                  <motion.div
                    variants={fadeInUp}
                    whileHover={{ y: -15 }}
                    className="process-card text-center"
                  >
                    <motion.div 
                      className="process-icon mb-4"
                      whileHover={{ scale: 1.2, rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      <i className={`bi ${step.icon}`}></i>
                    </motion.div>
                    <h5 className="process-title mb-3">{step.title}</h5>
                    <p className="process-description">{step.description}</p>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="cta-content">
              <motion.div
                className="cta-icon mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <i className="bi-rocket-takeoff"></i>
              </motion.div>
              <h2 className="cta-title mb-4">Ready to Transform Your Distribution?</h2>
              <p className="cta-description mb-4">
                Join thousands of distributors who have revolutionized their supply chain operations with NetSuite ERP
              </p>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/contact" style={{ textDecoration: "none" }}>
                  <Button className="btn-primary-custom btn-lg px-5 py-3">
                    <i className="bi-chat-dots me-2"></i>
                    Get Started Today
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <FAQSection />
    </div>
  );
};

export default WholesaleDistributionPage;